import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	AlertTriangle,
	ArrowRight,
	Check,
	Loader2,
	Wrench,
	X,
} from "lucide-react";
import { technicalAuditService } from "@/services/technicalAuditService";
import { readHreflangPreview } from "@/types/technicalAudit";
import type {
	HreflangChange,
	HreflangClusterEvidence,
	HreflangClusterPage,
	HreflangErrorDetails,
	HreflangPreviewPayload,
	PageIssue,
	TechnicalAuditFixPreview,
} from "@/types/technicalAudit";

interface HreflangCardProps {
	issue: PageIssue;
}

// hreflang_error (Phase 1): reason codes invalid_lang / broken_target have a
// deterministic staged fix — the preview endpoint returns the exact before/after
// tag set (fix_kind "hreflang") and Apply rewrites the page's hreflang link tags
// in WordPress. missing_x_default stays recommendation-only (x-default needs the
// site's chosen default-language URL), so those rows keep the advisory body.
// A row can carry MORE than one reason: if it has at least one applyable code it
// is still auto-fixable, and outstanding_reason_codes / outstanding_note describe
// the remainder Apply does NOT fix (shown as a separate advisory block).
// The preview's is_auto_fixable is authoritative; this card auto-loads it (mirrors
// MultipleMetaCard) so the staged changes are visible inline.
//
// CLUSTER (two-page) fixes: a non_reciprocal row whose partner page is on the
// same site and passes the safety gates comes back with cluster:true + pages
// ([issue page A, partner page B]) + evidence — Apply writes the hreflang tags
// to BOTH pages atomically. Because the user only selected ONE page, the card
// must make the two-page scope unmistakable (header + per-page sections) and
// show WHY it is safe (evidence strip). When the triage refuses (cross-domain
// partner, canonical conflict, same-language regional pair, thin content, …)
// the row stays recommendation-only and evidence.refusal_reason replaces the
// generic copy.
//
// Bug-1 note: the true fix mode ("replace" keeps valid tags vs "suppress" removes
// the whole set) is only known once the live page is fetched and the plan is
// computed. Until then the card uses mode-agnostic copy and NEVER claims the whole
// tag set is removed — suppress copy renders only for a computed suppress plan.

// Human label for each staged tag-set change type.
const CHANGE_TYPE_LABELS: Record<string, string> = {
	normalize_code: "Normalized",
	repoint: "Repointed",
	remove: "Removed",
};

const changeTypeLabel = (type: string): string =>
	CHANGE_TYPE_LABELS[type] || "Changed";

// Short human labels for each hreflang reason code, used when spelling out the
// Phase-2 remainder an auto-fixable row does NOT fix (outstanding_reason_codes).
const REASON_CODE_LABELS: Record<string, string> = {
	invalid_lang: "an invalid language/region code",
	broken_target: "a broken target URL",
	non_reciprocal: "a missing return link",
	missing_x_default: "a missing x-default alternate",
};

const reasonCodeLabel = (code: string): string =>
	REASON_CODE_LABELS[code] || code.replace(/_/g, " ");

// Why a recommendation-only row can't be auto-applied here, keyed by reason
// code. Fallback copy only — when the preview carries a specific
// evidence.refusal_reason (cluster triage) that replaces this.
const REASON_CODE_MANUAL_COPY: Record<string, string> = {
	non_reciprocal:
		"Fixing a missing return link means editing the paired page too, not just this one. When the partner page is on the same site and passes the safety checks (language verified, canonical OK, indexable), Apply fixes both pages together automatically. This row has no qualifying two-page fix staged, so it is saved as a recommendation and does not change WordPress.",
	missing_x_default:
		"Adding a missing x-default alternate means choosing the site's default-language URL for searchers whose language isn't listed — a site-wide decision, not a single-page edit. That stays manual, so this is saved as a recommendation and does not change WordPress.",
};

// Human labels for the machine safety gates in a cluster fix's evidence.
// Unknown gate keys fall back to a de-underscored version of the key.
const EVIDENCE_GATE_LABELS: Record<string, string> = {
	partner_same_install: "Same site",
	canonical_ok: "Canonical OK",
	noindex_ok: "Indexable",
	language_verified: "Language verified",
};

const gateLabel = (key: string): string =>
	EVIDENCE_GATE_LABELS[key] || key.replace(/_/g, " ");

// "es" / "spanish" -> "Spanish"; confidence 0.98 or 98 -> "98%".
const formatDetectedLanguage = (language: string, confidence: number): string => {
	const name = language.charAt(0).toUpperCase() + language.slice(1);
	const pct = Math.round(confidence <= 1 ? confidence * 100 : confidence);
	return `${name} ${pct}%`;
};

// The compact "language verified: Spanish 98%" suffix for the evidence strip —
// unique languages across the cluster's detected_languages, in payload order.
const detectedLanguagesSummary = (
	evidence: HreflangClusterEvidence,
): string | null => {
	const detected = evidence.detected_languages ?? [];
	if (detected.length === 0) return null;
	const seen = new Set<string>();
	const parts: string[] = [];
	for (const d of detected) {
		const key = d.language.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		parts.push(formatDetectedLanguage(d.language, d.confidence));
	}
	return parts.length > 0 ? parts.join(", ") : null;
};

// One hreflang alternate as a code-styled `hreflang → href` row.
function EntryRow({
	hreflang,
	href,
	muted,
}: {
	hreflang: string;
	href: string;
	muted?: boolean;
}) {
	return (
		<code
			className={`inline-flex flex-wrap items-center gap-1.5 text-xs ${
				muted ? "text-muted-foreground line-through" : "text-foreground"
			}`}
		>
			<span className="font-semibold">{hreflang}</span>
			<span aria-hidden>→</span>
			<span className="break-all">{href}</span>
		</code>
	);
}

// One annotated change: before entry → after entry (or removal).
function ChangeRow({ change }: { change: HreflangChange }) {
	const isRemoval = change.type === "remove" || !change.to;
	return (
		<div className="rounded-lg border border-teal/30 bg-card p-3">
			<div className="flex items-center gap-2">
				<Badge
					variant="outline"
					className={
						isRemoval
							? "border-destructive/30 bg-destructive/5 text-destructive"
							: "border-teal/40 bg-teal-light text-teal-dark"
					}
				>
					{changeTypeLabel(change.type)}
				</Badge>
			</div>
			<div className="mt-2 space-y-1">
				<EntryRow
					hreflang={change.from.hreflang}
					href={change.from.href}
					muted
				/>
				<div className="flex items-start gap-1.5">
					<ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-dark" />
					{change.to ? (
						<EntryRow hreflang={change.to.hreflang} href={change.to.href} />
					) : (
						<span className="text-xs text-muted-foreground">
							(tag removed)
						</span>
					)}
				</div>
			</div>
		</div>
	);
}

// One page of a two-page (cluster) fix: its URL, role badge, and the exact
// before/after tag set Apply writes on it. Reuses EntryRow so tags render the
// same way as in single-page plans.
function ClusterPageSection({
	page,
	index,
}: {
	page: HreflangClusterPage;
	index: number;
}) {
	const isPartner = index > 0;
	return (
		<div
			data-testid={`hreflang-cluster-page-${index}`}
			className="rounded-lg border border-teal/30 bg-card p-3"
		>
			<div className="flex flex-wrap items-center gap-2">
				<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					Page {index + 1}
				</p>
				{isPartner ? (
					<Badge
						variant="outline"
						className="border-coral/40 bg-coral-light text-coral-dark"
					>
						Partner page — return link added
					</Badge>
				) : (
					<Badge
						variant="outline"
						className="border-teal/40 bg-teal-light text-teal-dark"
					>
						The page you selected
					</Badge>
				)}
			</div>
			<p className="mt-1 break-all text-xs text-foreground/80">{page.url}</p>
			<div className="mt-2 space-y-2">
				<div className="space-y-1">
					<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
						Before
					</p>
					{page.before_entries.length > 0 ? (
						page.before_entries.map((entry, idx) => (
							<div key={`before-${entry.hreflang}-${idx}`}>
								<EntryRow hreflang={entry.hreflang} href={entry.href} muted />
							</div>
						))
					) : (
						<p className="text-xs text-muted-foreground">(no hreflang tags)</p>
					)}
				</div>
				<div className="space-y-1">
					<p className="text-[10px] font-semibold uppercase tracking-wide text-teal-dark">
						After
					</p>
					{page.after_entries.length > 0 ? (
						page.after_entries.map((entry, idx) => (
							<div key={`after-${entry.hreflang}-${idx}`}>
								<EntryRow hreflang={entry.hreflang} href={entry.href} />
							</div>
						))
					) : (
						<p className="text-xs text-muted-foreground">(no hreflang tags)</p>
					)}
				</div>
			</div>
		</div>
	);
}

// Compact "Why this is safe" strip: one chip per machine safety gate (passed =
// teal check, failed = coral cross), with the detected-language evidence folded
// into the language_verified chip.
function EvidenceStrip({ evidence }: { evidence: HreflangClusterEvidence }) {
	const gates = Object.entries(evidence.gates);
	if (gates.length === 0) return null;
	const languageSummary = detectedLanguagesSummary(evidence);
	return (
		<div
			data-testid="hreflang-evidence-strip"
			className="rounded-lg border border-teal/30 bg-teal-light/40 p-3"
		>
			<p className="text-xs font-semibold uppercase tracking-wide text-teal-dark">
				Why this is safe
			</p>
			<div className="mt-1.5 flex flex-wrap gap-1.5">
				{gates.map(([key, passed]) => {
					const withLanguages =
						key === "language_verified" && passed && languageSummary
							? `${gateLabel(key)}: ${languageSummary}`
							: gateLabel(key);
					return (
						<span
							key={key}
							className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
								passed
									? "border-teal/40 bg-teal-light text-teal-dark"
									: "border-coral/40 bg-coral-light text-coral-dark"
							}`}
						>
							{passed ? (
								<Check className="h-3 w-3" aria-label="passed" />
							) : (
								<X className="h-3 w-3" aria-label="failed" />
							)}
							{withLanguages}
						</span>
					);
				})}
			</div>
		</div>
	);
}

export function HreflangCard({ issue }: HreflangCardProps) {
	const details = (issue.issue_details || {}) as HreflangErrorDetails;
	const fix = issue.proposed_fix as { recommendation?: string } | null;
	const reason = typeof details.reason === "string" ? details.reason : null;

	const [preview, setPreview] = useState<TechnicalAuditFixPreview | null>(null);
	const [plan, setPlan] = useState<HreflangPreviewPayload | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	// Auto-load the staged fix preview so the exact before/after tag set is
	// visible right here in the drawer (mirrors MultipleMetaCard). The preview's
	// is_auto_fixable decides which body renders — fix plan vs. advisory.
	useEffect(() => {
		let ignore = false;
		// Defer the state kickoff out of the synchronous effect body (same
		// pattern as ApplyFixPreviewDialog's preview fetch).
		Promise.resolve().then(() => {
			if (ignore) return;
			setIsLoading(true);
			setLoadError(null);
			setPreview(null);
			setPlan(null);

			technicalAuditService
				.previewFix(issue.id)
				.then((data) => {
					if (ignore) return;
					setPreview(data);
					setPlan(readHreflangPreview(data.planned_payload));
				})
				.catch((err: unknown) => {
					if (ignore) return;
					const detail =
						(
							err as {
								response?: { data?: { detail?: string } };
								message?: string;
							}
						).response?.data?.detail ||
						(err as { message?: string }).message ||
						"Failed to load the staged hreflang fix";
					setLoadError(detail);
				})
				.finally(() => {
					if (!ignore) setIsLoading(false);
				});
		});

		return () => {
			ignore = true;
		};
	}, [issue.id]);

	const reasonCode = plan?.reason_code ?? details.reason_code;
	const isAutoFixable = Boolean(preview?.is_auto_fixable && plan);
	// The preview is authoritative: a loaded is_auto_fixable:false means this row
	// only saves a recommendation. reason_code distinguishes the Phase 2
	// return-link / x-default cases from any other exclusion.
	const isExcludedFromApply = preview !== null && !preview.is_auto_fixable;
	const isNonReciprocal = reasonCode === "non_reciprocal" || isExcludedFromApply;
	// Cluster triage refusal: the specific gate that failed (cross-domain
	// partner, canonical conflict, same-language regional pair, thin content, …).
	// When present it REPLACES the generic recommendation-only copy.
	const refusalReason =
		(isExcludedFromApply && plan?.evidence?.refusal_reason) || null;
	const excludedReason =
		preview?.auto_fix_excluded_reason ||
		preview?.manual_reason ||
		(reasonCode ? REASON_CODE_MANUAL_COPY[reasonCode] : null) ||
		null;
	const excludedBadgeLabel = refusalReason
		? "Not auto-applied — safety checks not met"
		: reasonCode === "non_reciprocal"
			? "Return-link fix — recommendation only for now"
			: reasonCode === "missing_x_default"
				? "x-default setup — recommendation only for now"
				: "Not auto-applied — saved as a recommendation";
	// A row "came from a real computed plan" once the live page was fetched and
	// the exact before/after tag set was resolved — evidenced by concrete changes
	// or a resolved after set. A static (fetch-free) preview has neither, so its
	// default mode is NOT authoritative and must not drive suppress copy.
	const hasComputedPlan = Boolean(
		plan && (plan.changes.length > 0 || plan.after_entries.length > 0),
	);
	// Bug 1: only assert "the whole tag set is removed" when the COMPUTED plan
	// actually says mode "suppress". Never infer suppress from an empty after set
	// on a fetch-free/uncomputed preview.
	const isSuppress =
		isAutoFixable && hasComputedPlan && plan?.mode === "suppress";
	// A replace/normalize plan whose live-fetched changes are resolved.
	const hasResolvedChanges =
		isAutoFixable && !isSuppress && (plan?.changes.length ?? 0) > 0;
	// Phase-2 remainder Apply does NOT fix on an otherwise auto-fixable row.
	const outstandingNote = plan?.outstanding_note ?? null;
	const outstandingCodes = plan?.outstanding_reason_codes ?? [];
	const hasOutstanding =
		isAutoFixable && (Boolean(outstandingNote) || outstandingCodes.length > 0);
	// CLUSTER (two-page) fix: Apply edits the selected page AND its partner page
	// atomically. Only trust the shape when both pages actually came through —
	// a malformed cluster payload falls back to the mode-agnostic single-page copy.
	const clusterPages =
		isAutoFixable && plan?.cluster === true && (plan.pages?.length ?? 0) >= 2
			? (plan.pages as HreflangClusterPage[])
			: null;
	const clusterEvidence = clusterPages ? (plan?.evidence ?? null) : null;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl border border-coral/40 bg-coral-light p-4">
				<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-coral/10">
					<AlertTriangle className="h-5 w-5 text-coral-dark" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-coral-dark">
						Hreflang issue detected
					</h4>
					<p className="mt-1 break-all text-sm text-foreground/80">
						{issue.page_url}
					</p>
					{reason && (
						<p className="mt-1 text-sm text-foreground/80">{reason}</p>
					)}
				</div>
			</div>

			{isLoading && (
				<div className="flex items-center gap-2 rounded-xl border border-border bg-warm-bg p-4 text-sm text-foreground/70">
					<Loader2 className="h-4 w-4 animate-spin" />
					Loading the staged hreflang fix…
				</div>
			)}

			{!isLoading && isAutoFixable && plan && (
				<Card
					data-testid="hreflang-fix-plan"
					className="border-teal/30 bg-teal-light/40 shadow-sm"
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-sm font-semibold text-teal-dark">
							<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-light">
								<Wrench className="h-4 w-4 text-teal-dark" />
							</div>
							What Apply changes
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3 pt-0">
						{clusterPages ? (
							<>
								<div
									data-testid="hreflang-cluster-header"
									className="rounded-lg border border-coral/40 bg-coral-light p-3"
								>
									<p className="text-sm font-semibold text-coral-dark">
										This fix updates 2 pages
									</p>
									<p className="mt-1 text-xs text-coral-dark">
										You selected one page, but hreflang return links must match
										on both sides of the pair — so Apply edits both pages
										together in one step.
									</p>
									<ul className="mt-2 space-y-1">
										{clusterPages.map((page, idx) => (
											<li
												key={`${page.url}-${idx}`}
												className="break-all text-xs font-medium text-coral-dark"
											>
												{idx + 1}. {page.url}
												{idx === 0 ? " (the page you selected)" : " (partner page)"}
											</li>
										))}
									</ul>
								</div>
								{clusterPages.map((page, idx) => (
									<ClusterPageSection
										key={`${page.url}-${idx}`}
										page={page}
										index={idx}
									/>
								))}
								{clusterEvidence && (
									<EvidenceStrip evidence={clusterEvidence} />
								)}
							</>
						) : isSuppress ? (
							<>
								<div className="rounded-lg border border-teal/30 bg-card p-3">
									<p className="text-sm font-medium text-foreground">
										All hreflang tags will be removed from this page.
									</p>
									<p className="mt-1 text-xs text-foreground/70">
										The live page carries hreflang annotations it should not
										have (e.g. bogus hreflang on a single-language site) and
										nothing valid remains, so Apply removes the whole tag set
										instead of rewriting it.
									</p>
								</div>
								{plan.before_entries.length > 0 && (
									<div className="space-y-1 rounded-lg border border-teal/30 bg-card p-3">
										<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
											Tags removed
										</p>
										{plan.before_entries.map((entry, idx) => (
											<div key={`${entry.hreflang}-${idx}`}>
												<EntryRow
													hreflang={entry.hreflang}
													href={entry.href}
													muted
												/>
											</div>
										))}
									</div>
								)}
							</>
						) : hasResolvedChanges ? (
							plan.changes.map((change, idx) => (
								<ChangeRow
									key={`${change.type}-${change.from.hreflang}-${idx}`}
									change={change}
								/>
							))
						) : (
							// No computed plan yet (fetch-free / uncomputed preview): stay
							// mode-agnostic and NEVER claim the whole set is removed.
							<div className="rounded-lg border border-teal/30 bg-card p-3">
								<p className="text-sm font-medium text-foreground">
									Apply corrects this page&apos;s invalid hreflang tags.
								</p>
								<p className="mt-1 text-xs text-foreground/70">
									Apply re-checks this page&apos;s hreflang tags live, then
									corrects invalid language codes and removes or repoints
									broken targets. Valid tags are kept. If nothing valid
									remains, the whole set is removed.
								</p>
							</div>
						)}
						{hasOutstanding && (
							<div
								data-testid="hreflang-outstanding-note"
								className="rounded-lg border border-coral/40 bg-coral-light p-3"
							>
								<div className="flex items-center gap-2">
									<Badge
										variant="outline"
										className="border-coral/40 bg-coral-light text-coral-dark"
									>
										Partial fix — some items stay manual
									</Badge>
								</div>
								<p className="mt-2 text-xs text-coral-dark">
									{outstandingNote ||
										`Apply fixes the invalid/broken hreflang tags only. Still needs manual setup: ${outstandingCodes
											.map(reasonCodeLabel)
											.join(", ")}.`}
								</p>
							</div>
						)}
						{clusterPages ? (
							<p className="text-xs text-foreground/70">
								Apply rewrites the{" "}
								<code>&lt;link rel=&quot;alternate&quot; hreflang&gt;</code>{" "}
								tags on both pages in WordPress in one step. You confirm the
								exact before/after tag sets in the preview first.
							</p>
						) : (
							<p className="text-xs text-foreground/70">
								Apply rewrites the page&apos;s{" "}
								<code>&lt;link rel=&quot;alternate&quot; hreflang&gt;</code>{" "}
								tags in WordPress. You confirm the exact before/after tag set
								in the preview first.
							</p>
						)}
					</CardContent>
				</Card>
			)}

			{!isLoading && !isAutoFixable && (
				<Card className="border-border shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
							<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-light">
								<Wrench className="h-4 w-4 text-teal-dark" />
							</div>
							Recommended Action
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3 pt-0">
						<div className="rounded-lg border border-border bg-warm-bg p-3">
							<p className="text-sm font-medium text-foreground">
								Add or correct hreflang annotations for this page.
							</p>
							<ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground/80">
								<li>
									Each language/region version must reference every other
									version <em>and itself</em> with matching return tags
									(bidirectional).
								</li>
								<li>
									Use valid ISO codes (e.g. <code>en-us</code>,{" "}
									<code>fr-ca</code>) and absolute URLs.
								</li>
								<li>
									Set it consistently in the page <code>&lt;head&gt;</code>,
									the XML sitemap, or your SEO/multilingual plugin — not a mix.
								</li>
							</ul>
							{fix?.recommendation && (
								<p className="mt-2 text-sm text-foreground/80">
									{fix.recommendation}
								</p>
							)}
						</div>
						{isNonReciprocal ? (
							<>
								<div className="flex items-center gap-2">
									<Badge
										variant="outline"
										className="border-coral/40 bg-coral-light text-coral-dark"
									>
										{excludedBadgeLabel}
									</Badge>
								</div>
								{refusalReason ? (
									// The cluster triage's specific gate failure — shown
									// prominently in place of the generic Phase-2 copy.
									<div
										data-testid="hreflang-refusal-reason"
										className="rounded-lg border border-coral/40 bg-coral-light p-3"
									>
										<p className="text-xs font-medium text-coral-dark">
											{refusalReason}
										</p>
										<p className="mt-1 text-xs text-coral-dark/80">
											Saved as a recommendation — Apply does not change
											WordPress for this row.
										</p>
									</div>
								) : (
									<p className="text-xs text-muted-foreground">
										{excludedReason ||
											"This hreflang issue needs cross-page or site-wide setup, not a single-page tag rewrite, so it is saved as a recommendation and does not change WordPress."}
									</p>
								)}
							</>
						) : (
							// Preview unavailable (fetch failed / legacy row): stay neutral —
							// invalid-code and broken-target rows ARE applyable, so don't
							// claim "not auto-applied" here.
							<p className="text-xs text-muted-foreground">
								{loadError
									? "Couldn't load the staged fix preview. Invalid-code and broken-target errors can still be applied — use Apply to WordPress and confirm the before/after tag set in the preview."
									: "Use Preview before/after to see whether this row has a staged automatic fix."}
							</p>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
